import React from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles from "@material-ui/core/styles/withStyles";
import {
  AttainmentRiskIcon,
  PageHeader,
  PageRoot,
  ResponseDueIcon,
  TypeaheadSelect,
  Select
} from "../../components";
import { AttainmentRisk, CAM, CamStatus } from "../../constants/interfaces";
import routes from "../../constants/routes";
import { camActions } from "../../reducers/cam";
import RxTable, {
  Table,
  IColumn,
  TableDetailCard,
  SearchBar,
  CustomWidget,
  FilterBar
} from "../../components/RxTable";
import formatDate from "../../utils/formatDate";

const styles = (theme: Theme) =>
  createStyles({
    select: {
      marginBottom: theme.spacing(1)
    },
    toolBar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end"
    }
  });

type Props = RouteComponentProps & {
  classes?: any;
  cams: CAM[];
  error?: Error;
  loading: boolean;
  getCams: () => Promise<void>;
};

export const camStateParse = (value: string): string => {
  switch (value) {
    case CamStatus.RESPONSE_REQUIRED:
      return "Response Required";
    case CamStatus.ASSESSMENT_REQUIRED:
    case CamStatus.ASSESSMENT_IN_PROGRESS:
    case CamStatus.PEER_REVIEW_REQUIRED:
    case CamStatus.PEER_REVIEW_COMPLETED:
    case CamStatus.DECISION_REQUIRED:
    case CamStatus.ONLINE_RESPONSE_RECEIVED:
      return "Assessment in Progress";
    case CamStatus.CLOSED_RESOLVED:
      return "Closed Resolved";
    case CamStatus.CLOSED_UNRESOLVED:
      return "Closed Unresolved";
    default:
      return value;
  }
};

export const attainmentRiskFilterValueTransformFn = (value: string): string => {
  switch (value) {
    case AttainmentRisk.UA_CRITICAL: // ...same as case below
    case AttainmentRisk.PA_CRITICAL: {
      return "Critical";
    }
    case AttainmentRisk.UA_HIGH: // ...same as case below
    case AttainmentRisk.PA_HIGH: {
      return "High";
    }
    case AttainmentRisk.UA_MODERATE: // ...same as case below
    case AttainmentRisk.PA_MODERATE: {
      return "Moderate";
    }
    case AttainmentRisk.UA_LOW: // ...same as case below
    case AttainmentRisk.PA_LOW: {
      return "Low";
    }
    case AttainmentRisk.UA_NEGLIGIBLE: // ...same as case below
    case AttainmentRisk.PA_NEGLIGIBLE: {
      return "Negligible";
    }
    default: {
      return value;
    }
  }
};

class CamList extends React.Component<Props> {
  tableColumns: IColumn[];
  constructor(props: Props) {
    super(props);
    this.tableColumns = [
      {
        id: "legalEntityName",
        label: "Legal Entity",
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <TypeaheadSelect
            className={props.classes.fullWidthSelect}
            id="legalEntityName"
            key="legalEntityName"
            placeholder="Legal entity"
            label="Legal Entity"
            onChange={e =>
              onFilterChange({
                value: e ? e.value : "",
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            suggestions={meta.suggestions}
            value={filterValue || ""}
          />
        )
      },
      {
        id: "premisesname",
        label: "Premises",
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <TypeaheadSelect
            className={props.classes.fullWidthSelect}
            id="premisesName"
            key="premisesName"
            placeholder="Premises"
            label="Premises"
            onChange={e =>
              onFilterChange({
                value: e ? e.value : "",
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            suggestions={meta.suggestions}
            value={filterValue || ""}
          />
        )
      },
      {
        id: "auditDate",
        type: "date",
        label: "Audit Date",
        parse: formatDate.condensed,
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <Select
            className={props.classes.select}
            id="auditDate"
            key="auditDate"
            label="Audit Date"
            onChange={e =>
              onFilterChange({
                value: e.target.value,
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            options={meta.suggestions}
            value={filterValue || ""}
          />
        )
      },
      {
        id: "auditType",
        label: "Audit Type",
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <Select
            className={props.classes.select}
            id="auditType"
            key="auditType"
            label="Audit Type"
            onChange={e =>
              onFilterChange({
                value: e.target.value,
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            options={meta.suggestions}
            value={filterValue || ""}
          />
        )
      },
      {
        id: "criterionId",
        label: "Criterion",
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <Select
            className={props.classes.select}
            id="criterionId"
            key="criterionId"
            label="Criterion"
            onChange={e =>
              onFilterChange({
                value: e.target.value,
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            options={meta.suggestions}
            value={filterValue || ""}
          />
        )
      },
      {
        id: "responseDueDate",
        type: "date",
        label: "Response Due",
        parse: formatDate.condensed,
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <Select
            className={props.classes.select}
            id="responseDueDate"
            key="responseDueDate"
            label="Response Due"
            onChange={e =>
              onFilterChange({
                value: e.target.value,
                compare: (fValue: string, cellValue: string) =>
                  formatDate.condensed(fValue) === formatDate.condensed(cellValue)
              })
            }
            options={meta.suggestions}
            value={filterValue || ""}
          />
        ),
        renderCell: cell => <ResponseDueIcon dueDate={cell} />
      },
      {
        id: "attainmentRisk",
        label: "Attainment Risk",
        searchable: true,
        parse: attainmentRiskFilterValueTransformFn,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <Select
            className={props.classes.select}
            id="attainmentRisk"
            key="attainmentRisk"
            label="Attainment Risk"
            onChange={e =>
              onFilterChange({
                value: e.target.value,
                compare: (fValue: string, cellValue: string) =>
                  attainmentRiskFilterValueTransformFn(fValue) === attainmentRiskFilterValueTransformFn(cellValue)
              })
            }
            options={meta.suggestions}
            value={filterValue || ""}
          />
        ),
        renderCell: (risk: AttainmentRisk) => <AttainmentRiskIcon risk={risk} />
      },
      {
        id: "camStatus",
        label: "Status",
        searchable: true,
        parse: camStateParse,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <Select
            className={props.classes.select}
            id="camStatus"
            key="camStatus"
            label="Status"
            onChange={e =>
              onFilterChange({
                value: e.target.value,
                compare: (fValue: string, cellValue: string) =>
                  camStateParse(fValue) === camStateParse(cellValue)
              })
            }
            options={meta.suggestions}
            value={filterValue || ""}
          />
        )
      }
    ];
  }

  componentDidMount() {
    this.props.getCams();
  }

  render() {
    const { loading, cams, classes } = this.props;

    return (
      <PageRoot>
        <PageHeader label={routes.CAM_LIST.label} />

        <RxTable>
          <div className={classes.toolBar}>
            <SearchBar />
            <CustomWidget
              compare={(next, prev) =>
                next.rawData === prev.rawData && next.filters === prev.filters
              }
            >
              {({ dispatch, filters, columns, rawData }) => (
                <FilterBar
                  filters={filters}
                  dispatch={dispatch}
                  data={rawData}
                  columns={columns}
                />
              )}
            </CustomWidget>
          </div>

          <Table
            columns={this.tableColumns}
            loading={loading}
            sorting={{ sortDirection: "asc", id: "responseDueDate" }}
            data={cams}
            rowsPerPage={25}
            getRowId={row => row.caseId}
          />

          <TableDetailCard
            detailCardActions={[
              {
                label: "View",
                shouldHide: detail =>
                  detail.camStatus === CamStatus.RESPONSE_REQUIRED,
                onClick: cam => {
                  this.props.history.push(
                    `${routes.CAM_RESPONSE.url}${cam.caseId}?from=CAM_LIST`
                  );
                }
              },
              {
                label: "Response",
                shouldHide: detail =>
                  detail.camStatus !== CamStatus.RESPONSE_REQUIRED,
                onClick: cam => {
                  this.props.history.push(
                    `${routes.CAM_RESPONSE.url}${cam.caseId}?from=CAM_LIST`
                  );
                }
              }
            ]}
            columns={this.tableColumns}
          />
        </RxTable>
      </PageRoot>
    );
  }
}

const mapDispatchToProps = {
  getCams: camActions.getCams
};

const mapStateToProps = ({ cam }: any) => {
  return {
    cams: cam.cams,
    error: cam.error,
    loading: cam.loading
  };
};

export const styled = withStyles(styles)(CamList);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled);
